<script setup>
import { defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  items: { type: Array, default: () => [] },
  collapsed: { type: Boolean, default: false },
  filter: { type: String, default: 'all' },
  activeKey: { type: String, default: '' } // dikirim dari parent
})

const emit = defineEmits(['select','filterChange','toggleCollapse'])

// Helper function to get tooltip text
function getTooltipText(label) {
  if (props.collapsed) {
    return `${label} (${t('sidebar.doubleClickToOpen')})`
  }
  return t('sidebar.doubleClickToOpen')
}

function setActive(key) {
  emit('select', key)        // buka/focus tab di parent
  if (location.hash.replace(/^#\/?/, '') !== key) {
    location.hash = key      // sinkronkan URL: /#key
  }
}

function setFilter(val) { emit('filterChange', val) }
function toggleCollapse() { emit('toggleCollapse') }
</script>

<template>
  <aside class="sidebar" :class="{ collapsed: props.collapsed }">
    <div class="side-toolbar">
      <button class="tbbtn" :class="{active: props.filter==='all'}" @click="setFilter('all')" :title="t('sidebar.all')">
        <i class="bi bi-grid-fill"></i>
      </button>
      <button class="tbbtn" :class="{active: props.filter==='trip-tariff'}" @click="setFilter('trip-tariff')" :title="t('sidebar.tripTariff')">
        <i class="bi bi-folder-fill"></i>
      </button>
      <button class="tbbtn" :class="{active: props.filter==='report'}" @click="setFilter('report')" :title="t('sidebar.report')">
        <i class="bi bi-pencil-square"></i>
      </button>
      <button class="tbbtn" :class="{active: props.filter==='container'}" @click="setFilter('container')" :title="t('sidebar.containerMgmt')">
        <i class="bi bi-truck"></i>
      </button>
      <button class="tbbtn" :class="{active: props.filter==='master-system'}" @click="setFilter('master-system')" :title="t('sidebar.masterSystem')">
        <i class="bi bi-gear-fill"></i>
      </button>
      <button class="tbbtn collapse-btn" @click="toggleCollapse" :aria-pressed="props.collapsed" :title="t('sidebar.collapseSidebar')">
        <i class="bi" :class="props.collapsed ? 'bi-caret-right-square' : 'bi-caret-left-square'"></i>
      </button>
    </div>

    <nav class="menu" role="navigation" aria-label="Sidebar">
      <!-- Dashboard -->
      <div
        class="item root"
        :class="{ active: props.activeKey==='dashboard' }"
        :aria-current="props.activeKey==='dashboard' ? 'page' : null"
        @dblclick.prevent="setActive('dashboard')"
        :title="t('sidebar.dashboardTooltip')"
        role="button"
        tabindex="0"
        @keydown.enter="setActive('dashboard')"
        @keydown.space.prevent="setActive('dashboard')"
      >
        <i class="bi bi-house-door"></i>
        <span class="label">{{ t('sidebar.dashboard') }}</span>
      </div>

      <!-- Sections -->
      <div v-for="section in props.items" :key="section.key" class="group">
        <div class="section-title">
          <span class="label">{{ section.label }}</span>
        </div>
        <div class="group-body">
          <div
            v-for="child in section.children"
            :key="child.key"
            class="item"
            :class="{ active: props.activeKey===child.key }"
            :aria-current="props.activeKey===child.key ? 'page' : null"
            @dblclick.prevent="setActive(child.key)"
            :data-label="child.label"
            :title="getTooltipText(child.label)"
            role="button"
            tabindex="0"
            @keydown.enter="setActive(child.key)"
            @keydown.space.prevent="setActive(child.key)"
          >
            <i :class="['bi', child.icon || 'bi-dot']"></i>
            <span class="label">{{ child.label }}</span>
          </div>
        </div>
      </div>
    </nav>
  </aside>
</template>

<style scoped src="./style/Sidebar.css"></style>
